/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";

import { useDashboard } from "./hooks/useDashboard";
import { LabService, type LabMember } from "./services/labService";
import { useAuth } from "./contexts/useAuth";

import { LabCard } from "./components/LabCard";
import { CreateLabForm } from "./components/CreateLabForm";
import { Alert } from "./components/Alert";
import { AssignedAuditsPanel } from "./components/dashboard/AssignedAuditsPanel";
import { ChampionTransferModal } from "./components/ChampionTransferModal";
import { Card } from "./components/ui/Card";
import { MemberRemovalModal, type MemberRemovalModalRef } from "./components/MemberRemovalModal";
import { EditLabModal } from "./components/EditLabModal";
import { LabAnalytics } from "./components/LabAnalytics";
import { PageLoadError } from "./components/PageLoadError";
import { EstimateImpactReport } from "./components/lab-details/EstimateImpactReport";
import { type LabType, normalizeLabType } from "./types/lab";

interface Message {
  text: string;
  type: 'error' | 'success';
}

import { type OrganisationDetailsResponse } from "./services/organisationService";
import { PageHeader } from "./components/pages/PageHeader";

interface DashboardProps {
  organisation?: OrganisationDetailsResponse;
}

export const Dashboard: React.FC<DashboardProps> = ({ organisation }) => {
  const { dashboardData, isLoading, error, refetch } = useDashboard();
  const alertRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  
  const [message, setMessage] = useState<Message>({ text: '', type: 'error' });
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [transferLabId, setTransferLabId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('labs');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isChampion, setIsChampion] = useState<boolean>(false);
  const [canSendInvitations, setCanSendInvitations] = useState<boolean>(false);
  const [loadingCards, setLoadingCards] = useState<Set<string>>(new Set());
  const [teamMembers, setTeamMembers] = useState<LabMember[]>([]);
  const [teamLoading, setTeamLoading] = useState<boolean>(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{ member: LabMember | null, labName: string }>({ member: null, labName: '' });
  const [removalReason, setRemovalReason] = useState<string>('');
  const [notifyRemovedUser, setNotifyRemovedUser] = useState<boolean>(false);
  const [deletionConfirmName, setDeletionConfirmName] = useState<string>('');
  const [showEditFteModal, setShowEditFteModal] = useState<boolean>(false);
  const memberRemovalModalRef = useRef<MemberRemovalModalRef>(null);

  const allLabs = dashboardData.all_labs;
  const currentLab = dashboardData.current_lab;

  React.useEffect(() => {
    if (user) {
      setIsAdmin(user.is_organisation_admin || false);
      setCanSendInvitations(user.can_invite || false);
      // Check if user is L0 Champion of current lab
      setIsChampion(currentLab?.is_champion || false);
    } else {
      setIsAdmin(false);
      setCanSendInvitations(false);
      setIsChampion(false);
    }
  }, [user, currentLab]);

  React.useEffect(() => {
    if (!currentLab || isAdmin) {
      setTeamMembers([]);
      setTeamError(null);
      setTeamLoading(false);
      return;
    }

    let cancelled = false;
    const fetchMembers = async () => {
      try {
        setTeamLoading(true);
        setTeamError(null);
        const response = await LabService.getLabMembers(currentLab.id);
        if (cancelled) return;
        const members: LabMember[] = Array.isArray(response.members) ? response.members : [];
        const filteredMembers = members.filter((member: LabMember) => (member.lab_name ?? currentLab.name) === currentLab.name);
        setTeamMembers(filteredMembers);
      } catch (err: unknown) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load team members';
        setTeamError(message);
        setTeamMembers([]);
      } finally {
        if (!cancelled) {
          setTeamLoading(false);
        }
      }
    };

    void fetchMembers();

    return () => {
      cancelled = true;
    };
  }, [currentLab?.id, currentLab?.member_count, currentLab?.name, isAdmin]);

  const scrollToAlert = (): void => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const setCardLoading = (labId: string, loading: boolean): void => {
    setLoadingCards(prev => {
      const newSet = new Set(prev);
      if (loading) {
        newSet.add(labId);
      } else {
        newSet.delete(labId);
      }
      return newSet;
    });
  };

  const handleCreateLab = async (formData: { name: string; total_fte: number; type_name: LabType; champion_id?: string; }): Promise<void> => {
    setActionLoading(true);
    setMessage({ text: '', type: 'error' });
    try {
      const result = await LabService.createLab(formData);
      setMessage({ text: result.message ?? 'Lab created', type: 'success' });
      setTimeout(scrollToAlert, 100);
      setShowCreateForm(false);
      await refetch();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Couldn't create lab";
      setMessage({ text, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setActionLoading(false);
    }
  };

  const handleJoinLab = async (labId: string): Promise<void> => {
    setCardLoading(labId, true);
    try {
      const result = await LabService.joinLab(labId);
      setMessage({ text: result.message ?? 'Lab joined', type: 'success' });
      setTimeout(scrollToAlert, 100);
      await refetch();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Couldn't join lab";
      setMessage({ text, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setCardLoading(labId, false);
    }
  };

  const handleUnjoinLab = async (labId: string): Promise<void> => {
    setCardLoading(labId, true);
    try {
      const result = await LabService.leaveLab(labId);
      setMessage({ text: result.message ?? 'Lab unjoined', type: 'success' });
      setTimeout(scrollToAlert, 100);
      await refetch();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Couldn't leave lab";
      setMessage({ text, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setCardLoading(labId, false);
    }
  };

  const handleTransferChampionAndLeave = async (labId: string, newChampionId: string): Promise<void> => {
    setActionLoading(true);
    try {
      const transferResult = await LabService.transferChampion(labId, newChampionId);
      const leaveResult = await LabService.leaveLab(labId);
      const successText = leaveResult.message ?? transferResult.message ?? 'Champion transferred';
      setMessage({ text: successText, type: 'success' });
      setTimeout(scrollToAlert, 100);
      setShowTransferModal(false);
      setTransferLabId(null);
      await refetch();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Couldn't transfer champion";
      setMessage({ text, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTransferChampion = async (newChampionId: string): Promise<void> => {
    if (!transferLabId) {
      setMessage({ text: 'No lab selected for champion transfer.', type: 'error' });
      setTimeout(scrollToAlert, 100);
      return;
    }

    await handleTransferChampionAndLeave(transferLabId, newChampionId);
  };

  const handleRemoveLab = async (labId: string): Promise<void> => {
    setCardLoading(labId, true);
    try {
      const result = await LabService.deleteLab(labId);
      setMessage({ text: result.message ?? 'Lab removed', type: 'success' });
      setTimeout(scrollToAlert, 100);
      await refetch();
    } catch (err) {
      const text = err instanceof Error ? err.message : "Couldn't remove lab";
      setMessage({ text, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setCardLoading(labId, false);
      setConfirmRemove(null);
    }
  };

  const confirmRemoveLab = (labId: string): void => {
    setDeletionConfirmName('');
    setConfirmRemove(labId);
  };

  const cancelRemove = (): void => {
    setConfirmRemove(null);
    setDeletionConfirmName('');
  };

  const handleTransferChampionClick = (labId: string): void => {
    setTransferLabId(labId);
    setShowTransferModal(true);
  };

  const handleRemoveMemberClick = (member: LabMember): void => {
    setRemovalReason('');
    setNotifyRemovedUser(false);
    setMemberToRemove({
      member,
      labName: currentLab?.name || member.lab_name || ''
    });
  };

  React.useEffect(() => {
    if (memberToRemove.member) {
      memberRemovalModalRef.current?.showModal();
    }
  }, [memberToRemove.member]);

  const handleConfirmRemoveMember = async (): Promise<void> => {
    if (!memberToRemove.member || !currentLab) return;

    try {
      const actorName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Organisation member';
      await LabService.removeLabMember(currentLab.id, memberToRemove.member.id, {
        reason: removalReason.trim() || undefined,
        notifyUser: notifyRemovedUser,
        actorId: user?.id ?? null,
        actorName,
        removedUserId: memberToRemove.member.id,
        organisationId: user?.organisation_id ?? null,
        labName: memberToRemove.labName || currentLab.name,
      });

      // Show success message
      const memberName = [memberToRemove.member.first_name, memberToRemove.member.last_name]
        .filter(Boolean).join(' ') || memberToRemove.member.email;
      setMessage({
        text: `Removed ${memberName} from ${memberToRemove.labName}.`,
        type: 'success'
      });
      setTimeout(scrollToAlert, 100);

      // Refresh team members list
      const response = await LabService.getLabMembers(currentLab.id);
      const members: LabMember[] = Array.isArray(response.members) ? response.members : [];
      const filteredMembers = members.filter((m: LabMember) =>
        (m.lab_name ?? currentLab.name) === currentLab.name
      );
      setTeamMembers(filteredMembers);

      // Reset state
      setMemberToRemove({ member: null, labName: '' });
      setRemovalReason('');
      setNotifyRemovedUser(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove team member';
      setMessage({ text: errorMessage, type: 'error' });
      setTimeout(scrollToAlert, 100);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <span className="loading loading-spinner loading-lg"></span>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <PageLoadError message={error} onRetry={refetch} />;
  }

  const otherLabs = currentLab ? allLabs.filter((lab) => lab.id !== currentLab.id) : allLabs;
  const assignedAudits = dashboardData.assigned_audits ?? [];

  const transferLab = transferLabId ? allLabs.find((lab) => lab.id === transferLabId) || currentLab : null;
  const canCreateLab: boolean = !currentLab || isAdmin;

  return (
    <>
      <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageHeader
          pageTitle={isAdmin ? 'Admin Dashboard' : 'Dashboard'}
          adminGuidePath={isAdmin ? "/admin-guide" : undefined}
          userGuidePath={!isAdmin ? "/user-guide" : undefined}
          backButton={
            !showCreateForm && canCreateLab && activeTab === 'labs' ? (
              <button
                className="btn btn-primary btn-sm h-10"
                onClick={() => setShowCreateForm(true)}
              >
                {isAdmin ? 'Create New Lab' : 'Create Lab'}
              </button>
            ) : undefined
          }
        >
          <div className="space-y-4">
            <p className="text-base-content/70 text-sm sm:text-base">
              {isAdmin ?
                'Manage labs in your organisation and view analytics' :
                dashboardData.current_lab ?
                  `Welcome to ${dashboardData.current_lab.name}` :
                  'Join or create a lab to get started'
              }
            </p>

            {/* Admin Tab Navigation */}
            {isAdmin && (
              <div className="tabs tabs-bordered">
                <button
                  className={`tab tab-bordered ${activeTab === 'labs' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('labs')}
                >
                  Lab Management
                </button>
                <button
                  className={`tab tab-bordered ${activeTab === 'analytics' ? 'tab-active' : ''}`}
                  onClick={() => setActiveTab('analytics')}
                >
                  Analytics
                </button>
              </div>
            )}
          </div>
        </PageHeader>



        {/* Alert */}
        <div ref={alertRef}>
          {message.text && (
            <Alert type={message.type} message={message.text} />
          )}
        </div>

        {/* Analytics Tab (Admin Only) */}
        {isAdmin && activeTab === 'analytics' && (
          <LabAnalytics />
        )}
        {/* Labs Tab */}
        {(!isAdmin || activeTab === 'labs') && (
          <>
            {/* Champion Transfer Modal */}
            {showTransferModal && transferLabId && transferLab && (
              <ChampionTransferModal
                labId={transferLabId}
                labName={transferLab.name}
                onTransfer={handleTransferChampion}
                onCancel={() => {
                  setShowTransferModal(false);
                  setTransferLabId(null);
                }}
                isLoading={actionLoading}
              />
            )}
            {/* Admin Confirm Remove Modal */}
            {isAdmin && confirmRemove && (
              <div className="modal modal-open">
                <div className="modal-box">
                  <h3 className="font-bold text-lg">Confirm Lab Removal</h3>
                  <p className="py-4">
                    Are you sure you want to delete this lab? This action cannot be undone.
                    All members will be removed from the lab.
                  </p>
                  
                  <div className="form-control w-full mb-4">
                    <label className="label">
                      <span className="label-text">
                        To confirm, type <strong>{allLabs.find(l => l.id === confirmRemove)?.name}</strong> in the box below:
                      </span>
                    </label>
                    <input 
                      type="text" 
                      placeholder="Type lab name here" 
                      className="input input-bordered w-full"
                      value={deletionConfirmName}
                      onChange={(e) => setDeletionConfirmName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="modal-action">
                    <button
                      className="btn btn-error"
                      onClick={() => {
                        handleRemoveLab(confirmRemove);
                        setDeletionConfirmName('');
                      }}
                      disabled={loadingCards.has(confirmRemove) || deletionConfirmName !== allLabs.find(l => l.id === confirmRemove)?.name}
                    >
                      {loadingCards.has(confirmRemove) ? (
                        <>
                          <span className="loading loading-spinner loading-sm"></span>
                          Deleting...
                        </>
                      ) : (
                        'Delete Lab'
                      )}
                    </button>
                    <button
                      className="btn btn-outline"
                      onClick={cancelRemove}
                      disabled={loadingCards.has(confirmRemove)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Create Lab Form */}
            {showCreateForm && (
              <CreateLabForm
                onSubmit={handleCreateLab}
                onCancel={() => setShowCreateForm(false)}
                isLoading={actionLoading}
                organisation={organisation}
              />
            )}

            {/* Current Lab Section */}
            {currentLab && (
              <Card variant="panel" className="p-6">
                <h2 className="text-xl font-semibold mb-4">My Lab</h2>
                <LabCard
                  lab={currentLab}
                  onJoin={handleJoinLab}
                  onUnjoin={handleUnjoinLab}
                  onTransferChampion={() => handleTransferChampionClick(currentLab.id)}
                  onRemove={isAdmin ? confirmRemoveLab : null}
                  isAdmin={isAdmin}
                  isLoading={loadingCards.has(currentLab.id)}
                  showDetails={true}
                />
                <div className="mt-6">
                  <EstimateImpactReport
                    labType={normalizeLabType(currentLab.type)}
                    labName={currentLab.name}
                    labId={currentLab.id}
                  />
                </div>
              </Card>
            )}

            {!isAdmin && currentLab && (
              <Card variant="panel" className="p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">Your Team</h2>
                    <p className="text-base-content/70 text-sm">People collaborating with you in {currentLab.name}</p>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm text-primary-content">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-content text-xs font-semibold md:h-8 md:w-8 shadow-md">
                      {teamMembers.length}
                    </span>
                  <span className="hidden sm:inline">Team members</span>
                    <span className="sm:hidden">Members</span>
                  </span>
                  {(isChampion || canSendInvitations) && (
                    <div className="flex gap-2">
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => setShowEditFteModal(true)}
                    >
                      Edit FTE
                    </button>
                    <Link
                      to={`/invitations?labId=${currentLab.id}&type=user`}
                      className="btn btn-primary btn-sm"
                    >
                      Invite Team Member
                    </Link>
                    </div>
                  )}
                </div>

                <div className="mt-4">
                  {teamLoading && (
                    <div className="flex items-center gap-2 text-base-content/70">
                      <span className="loading loading-spinner loading-sm" />
                      <span>Loading team members…</span>
                    </div>
                  )}

                  {!teamLoading && teamError && (
                    <div className="alert alert-error">
                      <span>{teamError}</span>
                    </div>
                  )}

                  {!teamLoading && !teamError && teamMembers.length === 0 && (
                    <p className="text-base-content/70 text-sm">
                      No teammates yet. Invite your team to join LAB ZER0.
                    </p>
                  )}

                  {!teamLoading && !teamError && teamMembers.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                      {teamMembers.map((member) => {
                        const nameParts = [member.first_name, member.last_name].filter(Boolean);
                        const displayName = nameParts.length > 0 ? nameParts.join(' ') : member.email;
                        const roleLabel = member.is_admin ? 'Admin' : member.is_champion ? 'L0 Champion' : 'Member';
                        const canRemoveMember = true
                        // const canRemoveMember = isChampion && !member.is_admin && !member.is_champion;

                        return (
                          <Card
                            key={member.id}
                            variant="subtle"
                            className="p-4 transition hover:-translate-y-1 hover:shadow-md"
                          >
                            <div className="space-y-2 mb-4">
                              <div className="flex justify-between text-sm">
                                <span className="text-base-content/70">Name:</span>
                                <span className="capitalize">{displayName}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-base-content/70">Email:</span>
                                <span>
                                  {member.email}
                                </span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-base-content/70">Role:</span>
                                <span className="truncate ml-2">{roleLabel}</span>
                              </div>
                              <div className="flex justify-between text-sm">
                                <span className="text-base-content/70">Lab:</span>
                                <span className="truncate ml-2">{member.lab_name ?? currentLab.name}</span>
                              </div>
                            </div>
                            {canRemoveMember && (
                              <div className="border-t border-[#eaddc9] pt-3">
                                <button
                                  onClick={() => handleRemoveMemberClick(member)}
                                  className="btn btn-sm btn-outline btn-error w-full"
                                >
                                  Remove
                                </button>
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            )}

            {!isAdmin && <AssignedAuditsPanel assignedAudits={assignedAudits} />}

            {/* Other Labs Section */}
            {otherLabs.length > 0 && (
              <Card variant="panel" className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  {isAdmin ? 'All Labs in Organisation' : 'Other Labs in Your Organisation'}
                </h2>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {otherLabs.map(lab => (
                    <LabCard
                      key={lab.id}
                      lab={lab}
                      onJoin={handleJoinLab}
                      onUnjoin={handleUnjoinLab}
                      onTransferChampion={lab.is_champion ? () => handleTransferChampionClick(lab.id) : null}
                      onRemove={isAdmin ? confirmRemoveLab : null}
                      isAdmin={isAdmin}
                      isLoading={loadingCards.has(lab.id)}
                    />
                  ))}
                </div>
              </Card>
            )}

            {/* No Labs Message */}
            {allLabs.length === 0 && !showCreateForm && (
              <Card variant="panel" className="p-8 text-center">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0V9a2 2 0 012-2h2a2 2 0 012 2v12" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">No Labs Found</h3>
                <p className="text-base-content/70 mb-4">
                  {isAdmin ?
                    'No labs have been created in your organisation yet. Create the first lab to get started.' :
                    'No labs are available in your organisation yet. Contact your administrator or create a new lab.'
                  }
                </p>
              </Card>
            )}
          </>
        )}
      </div>
      {/* Member Removal Modal */}
      {memberToRemove.member && (
        <MemberRemovalModal
          ref={memberRemovalModalRef}
          memberName={[memberToRemove.member.first_name, memberToRemove.member.last_name]
            .filter(Boolean).join(' ') || memberToRemove.member.email}
          labName={memberToRemove.labName}
          reason={removalReason}
          onReasonChange={setRemovalReason}
          notifyUser={notifyRemovedUser}
          onNotifyUserChange={setNotifyRemovedUser}
          handleRemoveMember={handleConfirmRemoveMember}
          handleCancel={() => {
            setMemberToRemove({ member: null, labName: '' });
            setRemovalReason('');
            setNotifyRemovedUser(false);
          }}
        />
      )}
    </div>
      {/* Edit Lab FTE Modal */}
      {showEditFteModal && currentLab && (
        <EditLabModal
          lab={currentLab!}
          isOpen={showEditFteModal}
          onClose={() => setShowEditFteModal(false)}
          onSuccess={() => {
            void refetch();
            setMessage({ text: 'Lab details updated successfully', type: 'success' });
          }}
        />
      )}
    </>
  );
};
